import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Pencil } from 'lucide-react'
import { useState } from 'react'

import { loanProductQueryOptions } from '@/api/loans-api'
import type { DualControlResult } from '@/api/types'
import { ErrorSurface } from '@/components/error-surface'
import { PageHeader } from '@/components/page-header'
import { RequiresWiderScreen } from '@/components/requires-wider-screen'
import { StatusBadge } from '@/components/status-badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { DualControlNotice, DualControlOutcome } from '@/features/loans/dual-control'
import { LoanLink } from '@/features/loans/loan-link'
import { loanPaths, useLoanNavigate, useLoanParams } from '@/features/loans/loan-paths'
import { useCreateLoanProduct, useUpdateLoanProduct } from '@/features/loans/mutations'
import { LOAN_PERMISSIONS, useLoanPermissions } from '@/features/loans/permissions'
import { LoanProductForm } from '@/features/loans/product-form'
import { LoanProductPanels } from '@/features/loans/product-panels'

/**
 * Reading a product, defining one, and amending one.
 *
 * Reading adapts to any width. Defining and amending refuse below `lg`: the mobile policy puts
 * product configuration in its refuse table — rarely urgent, permanently consequential — and the
 * form carries the rate, the fee schedule and the penalty rule that every loan written against
 * the product will inherit.
 */
export function LoanProductDetailPage() {
  const params = useLoanParams()
  const productId = params.productId ?? ''
  const permissions = useLoanPermissions()

  const query = useQuery(loanProductQueryOptions(productId))

  return (
    <>
      <LoanLink
        to={loanPaths.products}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-text hover:text-brand-text-hover max-lg:min-h-11 max-lg:py-2"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        All products
      </LoanLink>

      {query.isPending ? (
        <div className="space-y-4">
          <Skeleton className="h-10 w-80" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : query.isError ? (
        <ErrorSurface
          error={query.error}
          onRetry={() => void query.refetch()}
          title="This product could not be opened"
        />
      ) : (
        <>
          <PageHeader
            title={query.data.name}
            reference={query.data.code}
            description="Copied onto a round at application and frozen at approval. Amending it cannot reach a loan already approved."
            badges={<StatusBadge status={query.data.status} />}
            actions={
              permissions.canManageProducts ? (
                <Button asChild variant="secondary">
                  <LoanLink to={loanPaths.editProduct(query.data.id)}>
                    <Pencil />
                    Amend
                  </LoanLink>
                </Button>
              ) : null
            }
          />

          <LoanProductPanels product={query.data} />
        </>
      )}
    </>
  )
}

export function LoanProductNewPage() {
  const navigate = useLoanNavigate()
  const permissions = useLoanPermissions()
  const create = useCreateLoanProduct()
  const [result, setResult] = useState<DualControlResult | null>(null)

  return (
    <RequiresWiderScreen
      screen="Defining a loan product"
      reason="It sets the rate, the fees and the penalty rule every loan written against it will carry. Rarely urgent, permanently consequential."
    >
      <LoanLink
        to={loanPaths.products}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-text hover:text-brand-text-hover"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        All products
      </LoanLink>

      <PageHeader
        title="New loan product"
        description="Nothing can be lent on it until a second person countersigns it."
      />

      {permissions.canManageProducts ? (
        <LoanProductForm
          submitLabel="Submit the product"
          isPending={create.isPending}
          error={create.isError ? create.error : null}
          onDone={() => navigate(loanPaths.products)}
          notice={
            <DualControlNotice
              action="Defining a product"
              approvePermission={LOAN_PERMISSIONS.productManageApprove}
              canCountersign={permissions.canCountersignProduct}
            />
          }
          outcome={
            result ? (
              <DualControlOutcome
                result={result}
                awaitingDescription="The product is stored and does not exist yet. Nothing can be lent on it, and it will not appear in the product list."
                appliedDescription="The product exists and can take applications."
              />
            ) : null
          }
          onSubmit={(request) => {
            create.mutate(request, { onSuccess: setResult })
          }}
        />
      ) : (
        <PermissionNotice />
      )}
    </RequiresWiderScreen>
  )
}

export function LoanProductEditPage() {
  const params = useLoanParams()
  const productId = params.productId ?? ''
  const navigate = useLoanNavigate()
  const permissions = useLoanPermissions()
  const update = useUpdateLoanProduct(productId)
  const [result, setResult] = useState<DualControlResult | null>(null)

  const query = useQuery(loanProductQueryOptions(productId))

  return (
    <RequiresWiderScreen
      screen="Amending a loan product"
      reason="It reprices every loan written against the product from here on. Rarely urgent, permanently consequential."
    >
      <LoanLink
        to={productId === '' ? loanPaths.products : loanPaths.product(productId)}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-text hover:text-brand-text-hover"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        Back to the product
      </LoanLink>

      {query.isPending ? (
        <div className="space-y-4">
          <Skeleton className="h-10 w-80" />
          <Skeleton className="h-96 w-full max-w-3xl" />
        </div>
      ) : query.isError ? (
        <ErrorSurface
          error={query.error}
          onRetry={() => void query.refetch()}
          title="This product could not be opened"
        />
      ) : (
        <>
          <PageHeader
            title={`Amend ${query.data.name}`}
            reference={query.data.code}
            description="Terms are frozen onto a round at approval, so this cannot reach a loan somebody has already signed for."
            badges={<StatusBadge status={query.data.status} />}
          />

          <Alert tone="info">
            <AlertTitle>What an amendment can and cannot reach</AlertTitle>
            <AlertDescription>
              It prices applications made from now on. A round that has been approved carries its
              own copy of these terms and is untouched — that copy is what the member signed for,
              and it is visible on the round&apos;s terms tab.
            </AlertDescription>
          </Alert>

          {permissions.canManageProducts ? (
            <LoanProductForm
              product={query.data}
              submitLabel="Submit the amendment"
              isPending={update.isPending}
              error={update.isError ? update.error : null}
              onDone={() => navigate(loanPaths.product(productId))}
              notice={
                <DualControlNotice
                  action="Amending a product"
                  approvePermission={LOAN_PERMISSIONS.productManageApprove}
                  canCountersign={permissions.canCountersignProduct}
                />
              }
              outcome={
                result ? (
                  <DualControlOutcome
                    result={result}
                    awaitingDescription="The amendment is stored and the product is unchanged. Applications made now are still priced at the old terms."
                    appliedDescription="The product is amended. Applications made from now on carry the new terms; rounds already approved do not."
                  />
                ) : null
              }
              onSubmit={(request) => {
                update.mutate(request, { onSuccess: setResult })
              }}
            />
          ) : (
            <PermissionNotice />
          )}
        </>
      )}
    </RequiresWiderScreen>
  )
}

function PermissionNotice() {
  return (
    <Alert tone="warning">
      <AlertTitle>Your role cannot configure products</AlertTitle>
      <AlertDescription>
        This needs <span className="font-mono text-xs">{LOAN_PERMISSIONS.productManage}</span>, and
        a second person holding{' '}
        <span className="font-mono text-xs">{LOAN_PERMISSIONS.productManageApprove}</span> to
        countersign it.
      </AlertDescription>
    </Alert>
  )
}
